import { useMemo, useState } from 'react';
import { Alert, Badge, Container, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/common/PageMeta';
import { announcementService } from '../services/announcement.service';
import {
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS,
  type AnnouncementCategory,
} from '../constants/announcementCategories';
import { useAuth } from '../hooks/useAuth';
import { usePublicPathLocale } from '../hooks/usePublicPathLocale';

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** 研助全般のお知らせ一覧（公開・ログイン不要） */
export default function Announcements() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const pathLocale = usePublicPathLocale();
  const isBarePath = location.pathname.replace(/\/+$/, '') === '/announcements';
  const [category, setCategory] = useState<AnnouncementCategory | 'all'>('all');

  const lang = i18n.language.toLowerCase().startsWith('ja') ? 'ja' : 'en';
  const { data, isLoading, isError } = useQuery({
    queryKey: ['announcements', 'public', lang],
    queryFn: () => announcementService.listPublic({ lang, limit: 100 }),
    staleTime: 60 * 1000,
  });

  const rows = useMemo(() => {
    const all = data ?? [];
    return category === 'all' ? all : all.filter((row) => row.category === category);
  }, [data, category]);

  const categoryLabel = (key: AnnouncementCategory) =>
    t(`announcements.category_${key}`, { defaultValue: ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS[key] });

  return (
    <Container className="py-4" style={{ maxWidth: 880 }}>
      <PageMeta
        titleKey="announcements.title"
        descriptionKey="announcements.meta_description"
        path="announcements"
        localeAware={!isBarePath}
        localeAlternates={isBarePath}
        llmsDescribedBy
      />
      <h1 className="h3 mb-2">{t('announcements.title', { defaultValue: '研助からのお知らせ' })}</h1>
      <p className="text-muted">
        {t('announcements.lead', {
          defaultValue:
            '研助の各サービス（AIトレードアリーナ、FXドル円編のEA、AI競馬アリーナ、AI投資情報、計助などのツール）に関するお知らせです。',
        })}
      </p>
      <Alert variant="light" className="border small">
        {user ? (
          <>
            {t('announcements.signed_in_hint', {
              defaultValue: 'メールでの受け取りは、プロフィールの「お知らせメール」で種類ごとに変えられます。',
            })}{' '}
            <Link to="/profile">{t('announcements.open_profile', { defaultValue: '受信設定を開く' })}</Link>
          </>
        ) : (
          <>
            {t('announcements.register_hint', {
              defaultValue:
                'ユーザー登録（無料）をすると、EAの更新などのお知らせをメールで受け取れます。受け取る種類は登録後にいつでも変えられます。',
            })}{' '}
            <Link to="/register">{t('announcements.register_link', { defaultValue: 'ユーザー登録へ' })}</Link>
          </>
        )}
      </Alert>

      <div className="d-flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label={t('announcements.filter_label', { defaultValue: '種類で絞り込む' })}>
        <button
          type="button"
          role="radio"
          aria-checked={category === 'all'}
          className={`btn btn-sm ${category === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setCategory('all')}
        >
          {t('announcements.category_all', { defaultValue: 'すべて' })}
        </button>
        {ANNOUNCEMENT_CATEGORIES.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={category === key}
            className={`btn btn-sm ${category === key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setCategory(key)}
          >
            {categoryLabel(key)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : null}
      {isError ? (
        <Alert variant="danger">
          {t('announcements.load_error', { defaultValue: 'お知らせの読み込みに失敗しました。' })}
        </Alert>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? (
        <p className="text-muted">{t('announcements.empty', { defaultValue: 'お知らせはまだありません。' })}</p>
      ) : null}

      <ul className="list-unstyled d-grid gap-3">
        {rows.map((row) => (
          <li key={row.id} className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2 small text-muted">
                <time dateTime={row.publishedAt ?? undefined}>{formatDate(row.publishedAt, pathLocale)}</time>
                <Badge bg="secondary">{categoryLabel(row.category)}</Badge>
                {row.lang === 'ja' && lang !== 'ja' ? (
                  <span>{t('announcements.japanese_only', { defaultValue: '（日本語のみ）' })}</span>
                ) : null}
              </div>
              <h2 className="h5">{row.title}</h2>
              <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                {row.body}
              </p>
              {row.linkUrl ? (
                <a href={row.linkUrl} target={row.linkUrl.startsWith('/') ? undefined : '_blank'} rel="noopener noreferrer">
                  {t('announcements.open_link', { defaultValue: '詳しく見る' })}
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </Container>
  );
}
